import { useCallback, useEffect, useRef, useState } from "react"
import { NodeViewWrapper, NodeViewContent } from "@tiptap/react"
import { NodeSelection } from "@tiptap/pm/state"

import { isValidPosition } from "@/tiptap/lib/tiptap-utils"

function cssPropertyToReactName(propertyName) {
  return propertyName.replace(/-([a-z])/g, (_match, letter) => letter.toUpperCase())
}

function parseStyleAttribute(styleAttribute) {
  if (!styleAttribute) return {}

  return styleAttribute
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .reduce((styles, declaration) => {
      const separatorIndex = declaration.indexOf(":")
      if (separatorIndex === -1) return styles

      const propertyName = declaration.slice(0, separatorIndex).trim()
      const value = declaration.slice(separatorIndex + 1).trim()
      if (!propertyName || !value) return styles

      styles[cssPropertyToReactName(propertyName)] = value
      return styles
    }, {})
}

export function ImageNodeView(props) {
  const { editor, node, updateAttributes, getPos } = props
  const hasContent = node.content.size > 0
  // Prefer textContent for placeholder visibility — mobile IME can leave DOM
  // trailing-break chrome that keeps CSS :has(:empty) matching after typing.
  const isCaptionEmpty = !node.textContent

  return (
    <ResizableImage
      src={node.attrs.src}
      alt={node.attrs.alt || ""}
      imageStyle={node.attrs.style}
      editor={editor}
      align={node.attrs["data-align"]}
      initialWidth={node.attrs.width}
      showCaption={node.attrs.showCaption}
      hasContent={hasContent}
      isCaptionEmpty={isCaptionEmpty}
      nodeSize={node.nodeSize}
      onImageResize={(width) => updateAttributes({ width })}
      onUpdateAttributes={updateAttributes}
      getPos={getPos} />
  );
}

export const ResizableImage = ({
  src,
  alt = "",
  imageStyle,
  editor,
  minWidth = 96,
  maxWidth = 800,
  align = "left",
  initialWidth,
  showCaption = false,
  hasContent = false,
  isCaptionEmpty = true,
  nodeSize,
  onImageResize,
  onUpdateAttributes,
  getPos,
}) => {
  const [resizeParams, setResizeParams] = useState()
  const [width, setWidth] = useState(initialWidth)
  const [isNodeSelected, setIsNodeSelected] = useState(false)
  const isMountedRef = useRef(true)
  const wrapperRef = useRef(null)
  const imageRef = useRef(null)

  // TipTap caches node pos for ProseMirror-selectednode. Typing above an image
  // shifts pos without update(), so TipTap strips that class while NodeSelection
  // is still active (toolbar shows, outline doesn't). Track selection with
  // getPos() — always current for imperative checks.
  useEffect(() => {
    if (!editor) return

    const syncSelected = () => {
      if (!isMountedRef.current) return
      const pos = getPos?.()
      if (!isValidPosition(pos)) {
        setIsNodeSelected(false)
        return
      }

      const { selection } = editor.state
      setIsNodeSelected(
        selection instanceof NodeSelection && selection.from === pos
      )
    }

    syncSelected()
    editor.on("selectionUpdate", syncSelected)
    editor.on("update", syncSelected)
    return () => {
      editor.off("selectionUpdate", syncSelected)
      editor.off("update", syncSelected)
    };
  }, [editor, getPos])

  // Hide the empty caption placeholder when focus leaves the image.
  useEffect(() => {
    if (!editor || !showCaption) return

    const handleSelectionUpdate = () => {
      const pos = getPos()
      if (!isValidPosition(pos) || !nodeSize || !isMountedRef.current) return

      const { from, to } = editor.state.selection
      const isOutsideNode = to < pos || from > pos + nodeSize

      if (isOutsideNode && !hasContent && onUpdateAttributes) {
        onUpdateAttributes({ showCaption: false })
      }
    }

    editor.on("selectionUpdate", handleSelectionUpdate)
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, showCaption, hasContent, getPos, nodeSize, onUpdateAttributes])

  // Had to manually set the node selection on image click because
  // We treat the image-node-extension as content: "inline*"
  const handleImageClick = useCallback((event) => {
    if (!editor || !getPos || resizeParams) return

    event.preventDefault()
    event.stopPropagation()

    const pos = getPos()
    if (isValidPosition(pos)) {
      // scrollIntoView: false — default focus scrolls the *previous* caret
      // into view first (often far away on mobile).
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .setNodeSelection(pos)
        .run()
    }
  }, [editor, getPos, resizeParams])

  const windowMouseMoveHandler = useCallback(event => {
    if (!resizeParams || !editor || !isMountedRef.current) return

    const clientX =
      "touches" in event ? (event.touches[0]?.clientX ?? 0) : event.clientX
    const isLeftHandle = resizeParams.handleUsed === "left"
    const multiplier = align === "center" ? 2 : 1

    const delta = isLeftHandle
      ? (resizeParams.initialClientX - clientX) * multiplier
      : (clientX - resizeParams.initialClientX) * multiplier

    const newWidth = resizeParams.initialWidth + delta
    const effectiveMaxWidth =
      editor.view.dom?.firstElementChild?.clientWidth || maxWidth
    const clampedWidth = Math.min(Math.max(newWidth, minWidth), effectiveMaxWidth)

    setWidth(clampedWidth)
    if (wrapperRef.current) {
      wrapperRef.current.style.width = `${clampedWidth}px`
    }
  }, [editor, align, maxWidth, minWidth, resizeParams])

  const windowMouseUpHandler = useCallback(() => {
    if (!editor || !isMountedRef.current || !resizeParams) return

    if (isMountedRef.current) {
      setResizeParams(undefined)
    }
    onImageResize?.(width)

    // Always restore NodeSelection after resize. Selection is lost mid-drag
    // because the image node has content: "inline*".
    const pos = getPos()
    if (isValidPosition(pos) && isMountedRef.current) {
      editor
        .chain()
        .focus(undefined, { scrollIntoView: false })
        .setNodeSelection(pos)
        .run()
    }
  }, [editor, getPos, onImageResize, resizeParams, width])

  const startResize = useCallback((handleUsed, clientX) => {
    setResizeParams({
      handleUsed,
      initialWidth: wrapperRef.current?.clientWidth ?? minWidth,
      initialClientX: clientX,
    })
  }, [minWidth])

  const leftResizeHandleMouseDownHandler = (
    event
  ) => {
    event.preventDefault()
    event.stopPropagation()
    startResize("left", event.clientX)
  }

  const leftResizeHandleTouchStartHandler = (
    event
  ) => {
    event.stopPropagation()
    const touch = event.touches[0]
    if (touch) startResize("left", touch.clientX)
  }

  const rightResizeHandleMouseDownHandler = (
    event
  ) => {
    event.preventDefault()
    event.stopPropagation()
    startResize("right", event.clientX)
  }

  const rightResizeHandleTouchStartHandler = (
    event
  ) => {
    event.stopPropagation()
    const touch = event.touches[0]
    if (touch) startResize("right", touch.clientX)
  }

  useEffect(() => {
    window.addEventListener("mousemove", windowMouseMoveHandler)
    window.addEventListener("mouseup", windowMouseUpHandler)
    window.addEventListener("touchmove", windowMouseMoveHandler, {
      passive: false,
    })
    window.addEventListener("touchend", windowMouseUpHandler)

    return () => {
      window.removeEventListener("mousemove", windowMouseMoveHandler)
      window.removeEventListener("mouseup", windowMouseUpHandler)
      window.removeEventListener("touchmove", windowMouseMoveHandler)
      window.removeEventListener("touchend", windowMouseUpHandler)
    };
  }, [windowMouseMoveHandler, windowMouseUpHandler])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const shouldShowCaption = showCaption || hasContent
  const hasExplicitWidth = Boolean(width)
  const renderedImageStyle = {
    ...parseStyleAttribute(imageStyle),
    cursor: editor?.isEditable ? "pointer" : "default",
  }

  return (
    <NodeViewWrapper
      data-align={align}
      data-width={width}
      data-resized={hasExplicitWidth}
      data-resizing={Boolean(resizeParams)}
      data-selected={isNodeSelected || undefined}
      className="tiptap-image">
      <div
        ref={wrapperRef}
        className="tiptap-image-container"
        style={{ width: hasExplicitWidth ? `${width}px` : undefined }}>
        <div
          className="tiptap-image-content"
          data-drag-handle=""
          data-image-drag-handle="">
          <img
            ref={imageRef}
            src={src}
            alt={alt}
            className="tiptap-image-img"
            contentEditable={false}
            draggable={false}
            onClick={handleImageClick}
            style={renderedImageStyle} />

          {editor?.isEditable && (
            <>
              <div
                className="tiptap-image-handle tiptap-image-handle-left"
                onMouseDown={leftResizeHandleMouseDownHandler}
                onTouchStart={leftResizeHandleTouchStartHandler} />
              <div
                className="tiptap-image-handle tiptap-image-handle-right"
                onMouseDown={rightResizeHandleMouseDownHandler}
                onTouchStart={rightResizeHandleTouchStartHandler} />
            </>
          )}
        </div>

        {editor?.isEditable && shouldShowCaption && (
          <NodeViewContent
            as="div"
            className="tiptap-image-caption"
            data-placeholder="Add a caption..."
            data-empty={isCaptionEmpty ? "true" : "false"} />
        )}
      </div>
    </NodeViewWrapper>
  );
}
